import React, { useCallback, useEffect, useState } from 'react';

interface FontSizeInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  /** Optional title for accessibility */
  title?: string;
}

/**
 * Font size input with editable number field plus stacked +/- buttons.
 * - Up/Down arrow keys while focused increment/decrement by 1
 * - Stacked +/- buttons clamp to min/max
 * - Value commits on blur or Enter
 */
const FontSizeInput: React.FC<FontSizeInputProps> = ({
  value,
  onChange,
  min = 10,
  max = 80,
  disabled = false,
  title = 'Font size',
}) => {
  const [draft, setDraft] = useState<string>(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const clamp = useCallback((n: number) => Math.min(max, Math.max(min, n)), [min, max]);

  const commit = useCallback(() => {
    const parsed = parseInt(draft, 10);
    if (Number.isFinite(parsed)) {
      const next = clamp(parsed);
      if (next !== value) onChange(next);
      setDraft(String(next));
    } else {
      setDraft(String(value));
    }
  }, [draft, value, onChange, clamp]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      onChange(clamp(value + 1));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      onChange(clamp(value - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div
      className="flex items-stretch"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        type="text"
        inputMode="numeric"
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
        title={title}
        aria-label={title}
        className="w-7 h-6 text-[11px] leading-none text-center bg-black/40 text-white rounded-l border border-r-0 border-white/20 focus:outline-none focus:border-white/60"
        style={{ paddingInline: 0 }}
      />
      <div className="flex flex-col">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(clamp(value + 1)); }}
          disabled={disabled}
          aria-label="Increase font size"
          className="w-3 h-3 leading-none text-[10px] text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-tr border border-white/20 border-b-0 flex items-center justify-center"
        >
          +
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(clamp(value - 1)); }}
          disabled={disabled}
          aria-label="Decrease font size"
          className="w-3 h-3 leading-none text-[10px] text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-br border border-white/20 flex items-center justify-center"
        >
          −
        </button>
      </div>
    </div>
  );
};

export default FontSizeInput;
